import {Component, EventEmitter, Injector, Input, Output} from '@angular/core';
import {NgbActiveModal, NgbModal, NgbModalOptions, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {defaultTicketModalOptions} from '@app/layout/sites/tickets/ticket-modals/ticket-modal-util';
import {AlertTicket, SiteService, TicketId} from '@shared/services';
import {PagedModel} from '@shared/api/model/paged-model';
import {SiteId} from '@shared/services/api/access-sites.interface';
import {finalize, mergeMap} from "rxjs/operators";
import {AlertTicketDetailsModalComponent} from "@app/layout/sites/tickets/alert-ticket-details-modal/alert-ticket-details-modal.component";
import {TicketDataCaches} from "@shared/data-stores/ticket-data-caches";
import {TicketUtils} from "@app/layout/sites/tickets/ticket-utils";
import {from} from "rxjs";
import {TicketOperationResult} from "@app/layout/sites/tickets/tickets.interface";
import {I18nMessage} from "@shared/api/model/i18n";


export interface ViewAllAlertsLinkParams {
  site_id: SiteId;
}

export interface PaginationConfig {
  pageSize: number;
  page: number;
}

export interface AlertsListModalData {
  modalTitle?: I18nMessage | string | null;
  viewAllAlertsLinkParams?: ViewAllAlertsLinkParams;
  loading?: boolean;
  paginationConfig?: PaginationConfig | null;
  alertTicketsPage?: PagedModel<AlertTicket[]> | null;
}

@Component({
  selector: 'app-alerts-list-modal',
  templateUrl: './alerts-list-modal.component.html',
  styleUrls: ['./alerts-list-modal.component.scss'],
  standalone: false
})
export class AlertsListModalComponent implements AlertsListModalData {
  @Input() modalTitle: I18nMessage | string | null = null;
  @Input() viewAllAlertsLinkParams: ViewAllAlertsLinkParams;
  @Input() message: I18nMessage;
  @Input() loading = false;
  @Input() paginationConfig: PaginationConfig | null = null;
  @Input() set alertTicketsPage(alertTicketsPage: PagedModel<AlertTicket[]>) {
    this._alertTicketsPage = alertTicketsPage;
    const moreTicketCount = (this.alertTicketsPage?.count ?? 0) - (this.alertTicketsPage?.data?.length ?? 0);
    if (moreTicketCount < 0) {
      console.error('More ticket count cannot be negative, currently set to:', moreTicketCount);
      this.moreTicketCount = 0;
    } else {
      this.moreTicketCount = moreTicketCount;
    }
  }

  get alertTicketsPage(): PagedModel<AlertTicket[]> {
    return this._alertTicketsPage;
  }

  @Output() ticketOperation = new EventEmitter<TicketOperationResult<AlertTicket>>;
  @Output() pageEvent = new EventEmitter<number>();

  moreTicketCount: number = 0;
  loadingTicketIds = new Set<TicketId>();

  private ticketModalInjector: Injector;
  private _alertTicketsPage: PagedModel<AlertTicket[]>;

  static openInModal(
    modalService: NgbModal,
    data: AlertsListModalData,
    ngbOptions?: NgbModalOptions
  ): NgbModalRef {
    const modalRef = modalService.open(AlertsListModalComponent, {
      ...defaultTicketModalOptions,
      ...ngbOptions,
    });
    const modalInstance: AlertsListModalComponent = modalRef.componentInstance;
    modalInstance.modalTitle = data.modalTitle || '';
    modalInstance.viewAllAlertsLinkParams = data.viewAllAlertsLinkParams ?? modalInstance.viewAllAlertsLinkParams;
    modalInstance.loading = data.loading ?? modalInstance.loading;
    modalInstance.paginationConfig = data.paginationConfig ?? modalInstance.paginationConfig;
    modalInstance.alertTicketsPage = data.alertTicketsPage ?? modalInstance.alertTicketsPage;
    return modalRef;
  }

  constructor(
    protected activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private siteService: SiteService,
    private ticketDataCaches: TicketDataCaches,
    private injector: Injector
  ) {
  }

  protected setPage(page: number): void {
    this.pageEvent.emit(page);
  }

  protected openTicketDetailsModal(ticket: AlertTicket) {
    this.loadingTicketIds.add(ticket.ticket_id);
    this.siteService.getCachedSiteInfo(ticket.site_id).pipe(
      mergeMap((site => {
        const modalRef = AlertTicketDetailsModalComponent.openInModal(
          this.modalService,
          {
            ticket,
            site,
            showSiteName: false,
            showOtherEscalatedTicketsCount: true,
            showOtherEscalatedTicketsCountAsLink: true,
          },
          {
            injector: this.getTicketModalInjector(),
          });
        return from(modalRef.result);
      })),
      finalize(() => this.loadingTicketIds.delete(ticket.ticket_id))
    ).subscribe({
      next: ticketOperationResult => {
        if (ticketOperationResult) {
          this.ticketOperation.emit(ticketOperationResult);
        }
      },
      error: () => {}
    });
  }

  private getTicketModalInjector(): Injector {
    if (!this.ticketModalInjector) {
      this.ticketModalInjector = TicketUtils.makeTicketDataCachesInjector(this.ticketDataCaches, this.injector);
    }
    return this.ticketModalInjector;
  }
}
